// Reading the plan, and reacting to being refused by it.
//
// The database is the boundary (migration 20260822000700): every gate is
// enforced in `create_post` or in a trigger, and refuses with SQLSTATE PT402,
// which PostgREST returns as HTTP 402. Everything here is the pleasant half —
// disabling a control before the user commits to something, and turning a
// refusal into a sentence with an upgrade link instead of a red stack trace.
//
// No tier name, price or limit is written down in this file. They are rows.

import { supabase } from "./supabase";
import type {
  BillingInterval, Entitlements, FeatureEntitlement, PaywallDetail, Plan, PlanPrice,
  PricingCatalog, PricingRegion,
} from "./types";

/** Thrown instead of a bare Error when the database refused on plan grounds. */
export class PaywallError extends Error {
  readonly detail: PaywallDetail | null;

  constructor(message: string, detail: PaywallDetail | null) {
    super(message);
    this.name = "PaywallError";
    this.detail = detail;
  }
}

/**
 * Wraps a PostgREST error, preserving the PT402 signal that a plain
 * `new Error(error.message)` throws away.
 */
export function asAppError(error: { message: string; code?: string; details?: string }): Error {
  if (error.code === "PT402") {
    let detail: PaywallDetail | null = null;
    try {
      detail = error.details ? (JSON.parse(error.details) as PaywallDetail) : null;
    } catch {
      // The message alone is still useful.
    }
    return new PaywallError(error.message, detail);
  }
  return new Error(error.message);
}

export const isPaywall = (e: unknown): e is PaywallError => e instanceof PaywallError;

export async function loadEntitlements(workspaceId: string): Promise<Entitlements> {
  const { data, error } = await supabase.rpc("workspace_entitlements", {
    p_workspace_id: workspaceId,
  });
  if (error) throw asAppError(error);
  return data as Entitlements;
}

/** Every plan on offer, in display order. The pricing table renders whatever is here. */
export async function listPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from("plans")
    .select("*")
    .eq("is_public", true)
    .eq("is_active", true)
    .order("sort_order");
  if (error) throw asAppError(error);
  return data as Plan[];
}

/** What one plan grants, for the comparison table. */
export async function planMatrix(): Promise<
  Record<string, Record<string, { enabled: boolean; limit: number | null }>>
> {
  const { data, error } = await supabase
    .from("plan_entitlements")
    .select("plan_code, feature_key, enabled, limit_value");
  if (error) throw asAppError(error);

  const out: Record<string, Record<string, { enabled: boolean; limit: number | null }>> = {};
  for (const row of data as any[]) {
    (out[row.plan_code] ??= {})[row.feature_key] = {
      enabled: row.enabled,
      limit: row.limit_value === null ? null : Number(row.limit_value),
    };
  }
  return out;
}

export async function listFeatures(): Promise<
  Array<{ key: string; name: string; description: string | null; kind: string; unit: string | null;
          is_implemented: boolean; sort_order: number }>
> {
  const { data, error } = await supabase
    .from("features")
    .select("key, name, description, kind, unit, is_implemented, sort_order")
    .order("sort_order");
  if (error) throw asAppError(error);
  return data as any[];
}

// ---------------------------------------------------------------- reading

const NO_FEATURE: FeatureEntitlement = {
  name: "", kind: "boolean", unit: null, implemented: true,
  // A feature the app asks about before its row exists is allowed. The database
  // is still the gate; guessing "denied" here would hide working functionality
  // behind a paywall nobody configured.
  enabled: true, limit: null, used: null,
};

export function feature(ent: Entitlements | null, key: string): FeatureEntitlement {
  return ent?.features?.[key] ?? NO_FEATURE;
}

export const can = (ent: Entitlements | null, key: string): boolean =>
  feature(ent, key).enabled;

/** null means unlimited, and callers must treat it as such rather than as zero. */
export const limitOf = (ent: Entitlements | null, key: string): number | null =>
  feature(ent, key).limit;

export const usedOf = (ent: Entitlements | null, key: string): number =>
  feature(ent, key).used ?? 0;

/** Room left on a quota feature; null when unlimited. */
export function remaining(ent: Entitlements | null, key: string): number | null {
  const f = feature(ent, key);
  if (!f.enabled) return 0;
  if (f.limit === null) return null;
  return Math.max(0, f.limit - (f.used ?? 0));
}

/** Would this value be refused? Mirrors check_entitlement, for pre-flight UI only. */
export function withinLimit(
  ent: Entitlements | null,
  key: string,
  requested: number,
): boolean {
  const f = feature(ent, key);
  if (!f.enabled) return false;
  if (f.limit === null) return true;
  return f.kind === "quota"
    ? (f.used ?? 0) + requested <= f.limit
    : requested <= f.limit;
}

/** The last moment a scheduled post may be set for, or null when unlimited. */
export function schedulingHorizon(ent: Entitlements | null): Date | null {
  const days = limitOf(ent, "scheduling_horizon_days");
  if (days === null) return null;
  return new Date(Date.now() + days * 86_400_000);
}

// ---------------------------------------------------------------- formatting

export function formatBytesLimit(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v % 1 === 0 ? v : v.toFixed(1)} ${units[i]}`;
}

export function formatDuration(seconds: number): string {
  if (seconds >= 3600) {
    const h = seconds / 3600;
    return `${h % 1 === 0 ? h : h.toFixed(1)} h`;
  }
  if (seconds >= 60) return `${Math.round(seconds / 60)} min`;
  return `${seconds}s`;
}

/** One limit, rendered the way its unit deserves. "Unlimited" when null. */
export function formatLimit(limit: number | null, unit: string | null): string {
  if (limit === null) return "Unlimited";
  switch (unit) {
    case "bytes":   return formatBytesLimit(limit);
    case "seconds": return formatDuration(limit);
    case "days":    return limit === 0 ? "Not included" : `${limit} days`;
    default:        return `${limit.toLocaleString()}${unit ? ` ${unit}` : ""}`;
  }
}

export function formatPrice(plan: Plan): string {
  if (plan.price_cents === null || plan.price_cents === 0) return "Free";
  const amount = (plan.price_cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: plan.currency,
    minimumFractionDigits: plan.price_cents % 100 === 0 ? 0 : 2,
  });
  switch (plan.billing_period) {
    case "month":    return `${amount}/mo`;
    case "year":     return `${amount}/yr`;
    case "lifetime": return `${amount} once`;
    default:         return amount;
  }
}

/** The sentence shown when the database refuses, with the numbers filled in. */
export function paywallMessage(e: PaywallError): string {
  const d = e.detail;
  if (!d) return e.message;
  const name = d.feature_name ?? d.feature;

  switch (d.reason) {
    case "not_in_plan":
      return `${name} is not included in the ${d.plan} plan.`;
    case "quota_exceeded":
      return `You have used ${formatLimit(d.used, d.unit)} of your ` +
             `${formatLimit(d.limit, d.unit)} ${d.unit === "bytes" ? "storage" : ""}`.trim() +
             ` on the ${d.plan} plan.`;
    case "over_limit":
      return `${name} is capped at ${formatLimit(d.limit, d.unit)} on the ${d.plan} plan` +
             (d.requested != null ? ` — this one is ${formatLimit(d.requested, d.unit)}.` : ".");
    default:
      return e.message;
  }
}

// ---------------------------------------------------------------- pricing

/**
 * Regions, plans, prices, features and the entitlement matrix in one call.
 * Both clients read this same function, so the web and Flutter pricing pages
 * cannot drift apart.
 */
export async function pricingCatalog(): Promise<PricingCatalog> {
  const { data, error } = await supabase.rpc("pricing_catalog");
  if (error) throw asAppError(error);
  return data as PricingCatalog;
}

/**
 * Where we think the customer is — a guess, and labelled as one on the page.
 *
 * The browser will not tell us a country, so this reads the two things it does
 * expose: the locale's region subtag and the IANA time zone. Both are wrong for
 * a traveller or a VPN, which is exactly why the page lets the region be changed
 * by hand and why the disclaimer says the real answer comes from the billing
 * address and the payment processor.
 */
export function guessCountry(): string | null {
  try {
    const locale = navigator.languages?.[0] ?? navigator.language ?? "";
    const region = new Intl.Locale(locale).region;
    if (region) return region.toUpperCase();
  } catch {
    // Intl.Locale is unavailable or the tag is malformed; fall through.
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    if (/^Asia\/(Kolkata|Calcutta)$/.test(tz)) return "IN";
    if (/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|St_Johns|Regina|Montreal|Moncton|Whitehorse|Yellowknife|Iqaluit)$/.test(tz)) return "CA";
    if (/^(America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Indiana|Kentucky|Boise|Juneau|Sitka|Nome|Adak)|Pacific\/Honolulu)/.test(tz)) return "US";
  } catch {
    // No Intl at all. The default region covers it.
  }
  return null;
}

/** The region a country falls in, or the catch-all when it is in none of them. */
export function regionFor(regions: PricingRegion[], country: string | null): PricingRegion | null {
  if (!regions.length) return null;
  const match = country
    ? regions.find((r) => r.countries?.includes(country))
    : undefined;
  return match ?? regions.find((r) => r.is_default) ?? regions[0];
}

export function priceFor(
  catalog: PricingCatalog | null,
  planCode: string,
  regionCode: string,
  interval: BillingInterval,
): PlanPrice | null {
  return catalog?.prices.find(
    (p) => p.plan_code === planCode && p.region_code === regionCode &&
           p.billing_interval === interval,
  ) ?? null;
}

/** Amounts are in the currency's minor unit — cents, paise. */
export function formatMoney(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  try {
    return major.toLocaleString(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: major % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return `${currency} ${major.toLocaleString()}`;
  }
}

/**
 * How much a year of the yearly price saves against twelve of the monthly one.
 * Computed from the two rows rather than stored, so a price change cannot leave
 * a stale "save 20%" badge behind.
 */
export function yearlySavingPercent(
  catalog: PricingCatalog | null,
  planCode: string,
  regionCode: string,
): number | null {
  const monthly = priceFor(catalog, planCode, regionCode, "month");
  const yearly = priceFor(catalog, planCode, regionCode, "year");
  if (!monthly || !yearly || monthly.amount_minor === 0 || yearly.amount_minor === 0) return null;
  const full = monthly.amount_minor * 12;
  if (yearly.amount_minor >= full) return null;
  return Math.round(((full - yearly.amount_minor) / full) * 100);
}
